// src/components/IdeaForm.js
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { Sparkles, Loader2, AlertCircle } from 'lucide-react';
import StarRating from './StarRating';
import { encryptTag, decryptIfNeeded } from '../utils/encryption';
import { useEntitlementSummary } from '../context/EntitlementSummaryContext';
import {
  evaluateEnhancementEntitlementHint,
  getEntitlementFooterHint,
} from '../utils/enhancementEntitlementHint';
import { enhanceIdea, ENHANCEMENT_DENIAL_REFRESH_CODES } from '../utils/enhanceIdea';
import IdeaEnhancementModal from './IdeaEnhancementModal';

// Color name mapping
const COLOR_NAMES = {
  '#3B82F6': 'Blue',
  '#10B981': 'Green',
  '#F59E0B': 'Amber',
  '#EF4444': 'Red',
  '#8B5CF6': 'Purple',
  '#EC4899': 'Pink',
  '#06B6D4': 'Cyan'
};

const getColorName = (hexColor) => {
  if (!hexColor) return '';
  return COLOR_NAMES[hexColor] || hexColor;
};

function IdeaForm({ formData, setFormData, handleSubmit, handleCancel, hideCancel = false, autoFocus = true }) {
  const [user] = useAuthState(auth);
  const {
    summary: entitlementSummary,
    loading: entitlementLoading,
    error: entitlementError,
  } = useEntitlementSummary();
  const enhancementEntitlement = evaluateEnhancementEntitlementHint({
    summary: entitlementSummary,
  });
  const canUseEnhancement = enhancementEntitlement.allowed;
  const enhanceAccessHint = getEntitlementFooterHint({
    loading: entitlementLoading,
    summary: entitlementSummary,
    allowed: canUseEnhancement,
    snapshotError: entitlementError,
  });
  const [availableTags, setAvailableTags] = useState([]);
  const [newTagInput, setNewTagInput] = useState('');
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const descriptionRef = useRef(null);
  const ideaInputRef = useRef(null);
  const tagInputRef = useRef(null);
  
  // Enhancement states
  const [showEnhancementModal, setShowEnhancementModal] = useState(false);
  const [enhancementData, setEnhancementData] = useState(null);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhanceError, setEnhanceError] = useState(null);
  const [isRegenerating, setIsRegenerating] = useState(false);

  useEffect(() => {
    const fetchUserTags = async () => {
      if (!user) return;
      try {
        const tagsRef = collection(db, 'tags');
        const q = query(tagsRef, where('userId', '==', user.uid));
        const querySnapshot = await getDocs(q);
        const tags = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            name: decryptIfNeeded(data.name, process.env.REACT_APP_ENCRYPTION_KEY)
          };
        });
        setAvailableTags(tags);
        
        // Seed "dev" tag as default if tags are empty (only on initial load)
        // Use functional update to check current state
        setFormData(prevFormData => {
          const currentTags = Array.isArray(prevFormData.tags) ? prevFormData.tags : [];
          if (currentTags.length === 0) {
            // Check if "dev" tag exists
            const devTag = tags.find(tag => tag.name.toLowerCase() === 'dev');
            if (devTag) {
              // Use existing "dev" tag
              return { ...prevFormData, tags: [devTag.id] };
            }
            // If "dev" tag doesn't exist, create it
            (async () => {
              try {
                const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY;
                const encryptedTagData = encryptTag(
                  { name: 'dev', userId: user.uid },
                  encryptionKey
                );
                const docRef = await addDoc(tagsRef, {
                  ...encryptedTagData,
                  createdAt: serverTimestamp()
                });
                const newTag = {
                  id: docRef.id,
                  name: 'dev',
                  userId: user.uid
                };
                setAvailableTags(prev => [...prev, newTag]);
                setFormData(prev => ({ ...prev, tags: [docRef.id] }));
              } catch (error) {
                console.error('Error creating default dev tag:', error);
                // Continue without default tag if creation fails
              }
            })();
          }
          return prevFormData;
        });
      } catch (error) {
        console.error('Error fetching tags:', error);
      }
    };

    fetchUserTags();
  }, [user]); // Only run when user changes


  useEffect(() => {
    if (autoFocus && ideaInputRef.current) {
      ideaInputRef.current.focus();
    }
  }, [autoFocus]);

  const handleCreateTag = async (e) => {
    e.preventDefault();
    if (!user || !newTagInput.trim() || isCreatingTag) return;

    const tagName = newTagInput.trim();
    
    // Check if tag already exists
    const existingTag = availableTags.find(tag => 
      tag.name.toLowerCase() === tagName.toLowerCase()
    );
    
    if (existingTag) {
      // If tag exists, just select it
      handleTagChange(existingTag.id);
      setNewTagInput('');
      return;
    }

    try {
      setIsCreatingTag(true);
      const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY;
      
      // Encrypt the tag name before saving
      const encryptedTagData = encryptTag(
        { name: tagName, userId: user.uid },
        encryptionKey
      );

      // Create tag in Firestore
      const tagsRef = collection(db, 'tags');
      const docRef = await addDoc(tagsRef, {
        ...encryptedTagData,
        createdAt: serverTimestamp()
      });

      // Add to available tags and select it
      const newTag = {
        id: docRef.id,
        name: tagName, // Store decrypted name for display
        userId: user.uid
      };
      
      setAvailableTags(prev => [...prev, newTag]);
      
      // Add to formData tags
      const currentTags = Array.isArray(formData.tags) ? formData.tags : [];
      setFormData({ ...formData, tags: [...currentTags, docRef.id] });
      
      setNewTagInput('');
    } catch (error) {
      console.error('Error creating tag:', error);
      alert('Failed to create tag. Please try again.');
    } finally {
      setIsCreatingTag(false);
    }
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateTag(e);
    }
  };

  const handleTagChange = (tagId) => {
    const currentTags = Array.isArray(formData.tags) ? formData.tags : [];
    const newTags = currentTags.includes(tagId)
      ? currentTags.filter(id => id !== tagId)
      : [...currentTags, tagId];
    setFormData({ ...formData, tags: newTags });
  };

  const handleRatingChange = (rating) => {
    const ratingValue = rating === null || rating === undefined ? 0 : rating;
    setFormData({ ...formData, rating: ratingValue.toString() });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  // Enhancement handlers
  const handleEnhanceClick = async () => {
    if (!formData.description || formData.description.trim().length === 0) {
      setEnhanceError('Please enter a description to enhance the idea.');
      return;
    }

    setIsEnhancing(true);
    setEnhanceError(null);

    try {
      const enhancement = await enhanceIdea(formData.description, formData.idea);
      setEnhancementData(enhancement);
      setShowEnhancementModal(true);
    } catch (error) {
      console.error('Error enhancing idea:', error);
      if (error?.code && ENHANCEMENT_DENIAL_REFRESH_CODES.has(error.code) && auth.currentUser) {
        try {
          await auth.currentUser.getIdToken(true);
        } catch (_) {
          /* ignore */
        }
      }
      setEnhanceError(error.message || 'Failed to enhance idea. Please try again.');
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleRegenerate = async () => {
    if (!formData.description || formData.description.trim().length === 0) {
      return;
    }

    setIsRegenerating(true);
    setEnhanceError(null);

    try {
      const enhancement = await enhanceIdea(formData.description, formData.idea);
      setEnhancementData(enhancement);
    } catch (error) {
      console.error('Error regenerating enhancement:', error);
      if (error?.code && ENHANCEMENT_DENIAL_REFRESH_CODES.has(error.code) && auth.currentUser) {
        try {
          await auth.currentUser.getIdToken(true);
        } catch (_) {
          /* ignore */
        }
      }
      setEnhanceError(error.message || 'Failed to regenerate enhancement. Please try again.');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleApplyEnhancement = async (enhancedData) => {
    // Create or find tags by name and add to formData
    const tagIds = [];
    if (enhancedData.tags && Array.isArray(enhancedData.tags) && enhancedData.tags.length > 0) {
      for (const tagName of enhancedData.tags) {
        // Check if tag exists
        const existingTag = availableTags.find(tag => 
          tag.name.toLowerCase() === tagName.toLowerCase()
        );
        
        if (existingTag) {
          if (!tagIds.includes(existingTag.id)) {
            tagIds.push(existingTag.id);
          }
        } else {
          // Create new tag
          try {
            const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY;
            const encryptedTagData = encryptTag(
              { name: tagName, userId: user.uid },
              encryptionKey
            );
            const tagsRef = collection(db, 'tags');
            const docRef = await addDoc(tagsRef, {
              ...encryptedTagData,
              createdAt: serverTimestamp()
            });
            const newTag = {
              id: docRef.id,
              name: tagName,
              userId: user.uid
            };
            setAvailableTags(prev => [...prev, newTag]);
            tagIds.push(docRef.id);
          } catch (error) {
            console.error('Error creating tag:', error);
            // Continue even if tag creation fails
          }
        }
      }
    }

    // Convert subtasks from enhancement format {task, completed} to form format {text, completed, link}
    const convertedSubtasks = (enhancedData.subtasks || []).map(subtask => {
      if (typeof subtask === 'string') {
        return { text: subtask.trim(), completed: false, link: '' };
      }
      // Handle both {task, completed} and {text, completed, link} formats
      const taskText = subtask.task || subtask.text || '';
      return {
        text: taskText.trim(),
        completed: subtask.completed || false,
        link: subtask.link || ''
      };
    });

    // Format plan steps as text to append to description
    const formatPlanSteps = (planSteps) => {
      if (!planSteps || typeof planSteps !== 'object' || Object.keys(planSteps).length === 0) {
        return '';
      }

      let formatted = '\n\n## Plan Steps\n\n';
      Object.entries(planSteps).forEach(([category, steps]) => {
        if (Array.isArray(steps) && steps.length > 0) {
          formatted += `### ${category}\n`;
          steps.forEach(step => {
            if (step && typeof step === 'string') {
              formatted += `- ${step}\n`;
            }
          });
          formatted += '\n';
        }
      });
      return formatted.trim();
    };

    // Update formData with enhanced data
    // Use functional update to avoid stale closure issues
    setFormData(prevFormData => {
      // Get enhanced description
      let finalDescription = enhancedData.description !== undefined 
        ? enhancedData.description 
        : prevFormData.description || '';

      // Append plan steps if they exist
      const planStepsText = formatPlanSteps(enhancedData.planSteps);
      if (planStepsText) {
        finalDescription = finalDescription.trim();
        // Only append if not already present (avoid duplicates on re-apply)
        if (!finalDescription.includes('## Plan Steps')) {
          finalDescription += '\n\n' + planStepsText;
        }
      }

      const updatedFormData = {
        ...prevFormData,
        // Title: use enhanced title if provided, otherwise keep existing
        idea: enhancedData.title !== undefined ? enhancedData.title : prevFormData.idea,
        // Description: enhanced description with plan steps appended
        description: finalDescription,
        // Tags: merge with existing tags
        tags: [...new Set([...prevFormData.tags, ...tagIds])],
        // Subtasks: replace with new ones (not merge)
        subtasks: convertedSubtasks
      };
      return updatedFormData;
    });

    setShowEnhancementModal(false);
    setEnhancementData(null);
    setEnhanceError(null);
  };

  const selectedTags = Array.isArray(formData.tags) ? formData.tags : [];

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left Column - Idea and Description */}
        <div className="md:col-span-2 space-y-4">
          <div>
            <label htmlFor="idea" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Idea
            </label>
            <input
              ref={ideaInputRef}
              id="idea"
              name="idea"
              type="text"
              value={formData.idea}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              autoComplete="off"
              autoFocus={autoFocus}
              placeholder="Optional: Enter a title for your idea"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Description <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={handleEnhanceClick}
                disabled={
                  !canUseEnhancement ||
                  entitlementLoading ||
                  isEnhancing ||
                  !formData.description ||
                  formData.description.trim().length === 0
                }
                className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/30 disabled:hover:bg-purple-50 dark:disabled:hover:bg-purple-900/20"
                title={
                  !canUseEnhancement
                    ? enhanceAccessHint || "AI enhancement isn't available for your account right now. See Support."
                    : 'Enhance idea with AI'
                }
              >
                {isEnhancing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Enhancing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Enhance with AI
                  </>
                )}
              </button>
            </div>
            {!canUseEnhancement && !entitlementLoading && enhanceAccessHint && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{enhanceAccessHint}</p>
            )}
            {enhanceError && (
              <div className="mb-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-700 dark:text-red-300">{enhanceError}</p>
              </div>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              <Link to="/ai-tools" className="text-primary hover:underline font-medium">
                Read more about AI tools
              </Link>
              <span className="mx-1.5 text-gray-400" aria-hidden>
                ·
              </span>
              <Link to="/profile" className="text-primary hover:underline font-medium">
                Email verification (Account)
              </Link>
            </p>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              ref={descriptionRef}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none h-64"
              required
              autoComplete="off"
            />
          </div>

          {/* Lessons Learned - Only show when status is Archived or Paused */}
          {(formData.status === 'Archived' || formData.status === 'Paused') && (
            <div>
              <label htmlFor="lessonsLearned" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Lessons Learned
              </label>
              <textarea
                id="lessonsLearned"
                name="lessonsLearned"
                value={formData.lessonsLearned || ''}
                onChange={handleChange}
                rows={4}
                placeholder="What did we learn? (e.g., 'Anti-scraping was too tough', 'API cost too high')."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-amber-50 dark:bg-amber-900/20 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                autoComplete="off"
              />
            </div>
          )}
        </div>

        {/* Right Column - Tags, Rating, Save Button */}
        <div className="md:col-span-1 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tags <span className="text-red-500">*</span>
            </label>
            {availableTags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {availableTags.map((tag) => {
                  const isSelected = selectedTags.includes(tag.id);
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => handleTagChange(tag.id)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        isSelected
                          ? 'bg-primary text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                      }`}
                    >
                      {tag.name}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="mb-2">
              <input
                ref={tagInputRef}
                type="text"
                value={newTagInput}
                onChange={(e) => setNewTagInput(e.target.value)}
                onKeyDown={handleTagInputKeyDown}
                placeholder={availableTags.length === 0 ? "Type a tag name and press Enter to create it" : "Type and press Enter to create a new tag"}
                disabled={isCreatingTag}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
                autoComplete="off"
              />
              {isCreatingTag && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Creating tag...</p>
              )}
            </div>
            {selectedTags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-xs text-gray-600 dark:text-gray-400">Selected: </span>
                {selectedTags.map((tagId) => {
                  const tag = availableTags.find(t => t.id === tagId);
                  return tag ? (
                    <span key={tagId} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/20 text-primary">
                      {tag.name}
                    </span>
                  ) : null;
                })}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Status
            </label>
            <select
              id="status"
              name="status"
              value={formData.status || 'Drafted'}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="Drafted">Drafted</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Paused">Paused</option>
              <option value="Archived">Archived</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Rating
            </label>
            <StarRating
              value={formData.rating || 0}
              onChange={handleRatingChange}
              readOnly={false}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Color
            </label>
            <div className="flex flex-wrap gap-2">
              {[
                { value: '', label: 'None', color: 'bg-gray-200 dark:bg-gray-700' },
                { value: '#3B82F6', label: 'Blue', color: 'bg-blue-500' },
                { value: '#10B981', label: 'Green', color: 'bg-green-500' },
                { value: '#F59E0B', label: 'Amber', color: 'bg-amber-500' },
                { value: '#EF4444', label: 'Red', color: 'bg-red-500' },
                { value: '#8B5CF6', label: 'Purple', color: 'bg-purple-500' },
                { value: '#EC4899', label: 'Pink', color: 'bg-pink-500' },
                { value: '#06B6D4', label: 'Cyan', color: 'bg-cyan-500' }
              ].map((colorOption) => {
                const isSelected = (formData.color || '') === colorOption.value;
                return (
                  <button
                    key={colorOption.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, color: colorOption.value })}
                    className={`w-10 h-10 rounded-md border-2 transition-all ${
                      isSelected
                        ? 'border-gray-900 dark:border-white scale-110 shadow-md'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                    } ${colorOption.color}`}
                    title={colorOption.label}
                    aria-label={`Select ${colorOption.label} color`}
                  >
                    {colorOption.value === '' && (
                      <span className="text-gray-500 dark:text-gray-400 text-xs">×</span>
                    )}
                  </button>
                );
              })}
            </div>
            {formData.color && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Selected: {getColorName(formData.color)}
              </p>
            )}
          </div>

          <div className="space-y-3">
            <button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-3 px-6 rounded-md transition-colors text-lg"
            >
              Save
            </button>
            {!hideCancel && (
              <button
                type="button"
                onClick={handleCancel}
                className="w-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium py-2 px-6 rounded-md transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Enhancement Modal */}
      <IdeaEnhancementModal
        isOpen={showEnhancementModal}
        onClose={() => {
          setShowEnhancementModal(false);
          setEnhancementData(null);
          setEnhanceError(null);
        }}
        enhancement={enhancementData}
        onApply={handleApplyEnhancement}
        onRegenerate={handleRegenerate}
        isRegenerating={isRegenerating}
      />
    </form>
  );
}

export default IdeaForm;